import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  Save, 
  Loader2, 
  AlertCircle, 
  CheckCircle2, 
  GripVertical,
  Search
} from 'lucide-react';

const AdminAmenities = () => {
  const [amenities, setAmenities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const categories = ['Essentials', 'Outdoor', 'Kitchen', 'Entertainment', 'Services', 'Other'];

  useEffect(() => {
    fetchAmenities();
  }, []);

  const fetchAmenities = async () => {
    try {
      const { data, error } = await supabase
        .from('amenities')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });
      
      if (error) throw error;
      setAmenities(data || []);
    } catch (error) {
      console.error('Error fetching amenities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAmenity = () => {
    const newAmenity = {
      name: 'New Amenity',
      category: 'Essentials',
      icon_name: 'Check',
      is_active: true,
      isNew: true,
      tempId: Math.random().toString(36).substr(2, 9)
    };
    setAmenities([newAmenity, ...amenities]);
  };

  const handleChange = (id: string, field: string, value: any, isTemp = false) => {
    setAmenities(prev => prev.map(a => {
      const match = isTemp ? a.tempId === id : a.id === id;
      return match ? { ...a, [field]: value } : a;
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      for (const amenity of amenities) {
        const { isNew, tempId, ...saveData } = amenity;
        if (isNew) {
          const { error } = await supabase.from('amenities').insert([saveData]);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('amenities').update(saveData).eq('id', amenity.id);
          if (error) throw error;
        }
      }
      setMessage({ type: 'success', text: 'Amenities updated successfully!' });
      fetchAmenities();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, isTemp = false) => {
    if (!confirm('Are you sure?')) return;
    if (isTemp) {
      setAmenities(prev => prev.filter(a => a.tempId !== id));
      return;
    }
    try {
      const { error } = await supabase.from('amenities').delete().eq('id', id);
      if (error) throw error;
      setAmenities(prev => prev.filter(a => a.id !== id));
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const filteredAmenities = amenities.filter(a => 
    a.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    a.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-emerald" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-6">
          <div>
            <h1 className="text-4xl font-serif text-emerald">Amenities & Facilities</h1>
            <p className="text-[#59635C] font-clean mt-2">Manage property features and icons.</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={handleAddAmenity}
              className="bg-white text-emerald border border-emerald px-6 py-3 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/5 transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Amenity
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald text-ivory px-8 py-3 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/90 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save All
            </button>
          </div>
        </div>

        {message && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-clean font-medium">{message.text}</span>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-accent/40" />
          <input
            type="text"
            placeholder="Search amenities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-4 rounded-2xl border border-accent/10 focus:border-emerald outline-none font-clean bg-white shadow-sm"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAmenities.map((amenity) => (
            <div key={amenity.id || amenity.tempId} className="bg-white p-4 rounded-2xl border border-accent/10 shadow-sm flex items-center gap-4 group">
              <div className="shrink-0 w-10 h-10 bg-[#F5F1E8] rounded-xl flex items-center justify-center">
                <GripVertical className="w-4 h-4 text-accent/30" />
              </div>
              <div className="flex-grow space-y-2">
                <input
                  type="text"
                  value={amenity.name}
                  onChange={(e) => handleChange(amenity.id || amenity.tempId, 'name', e.target.value, !!amenity.isNew)}
                  className="w-full font-clean font-bold text-emerald border-none focus:ring-0 p-0 bg-transparent"
                  placeholder="Amenity Name"
                />
                <select
                  value={amenity.category}
                  onChange={(e) => handleChange(amenity.id || amenity.tempId, 'category', e.target.value, !!amenity.isNew)}
                  className="text-[10px] font-bold uppercase tracking-widest text-accent border-none bg-transparent p-0 outline-none"
                >
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <button 
                onClick={() => handleDelete(amenity.id || amenity.tempId, !!amenity.isNew)}
                className="p-2 text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-600 transition-all"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAmenities;
